import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@material-ui/core";
import React, { useState } from "react";
import FormGridCell from "../../common/FormGridCell";
import { OptionType } from "../../common/customSelect/CustomSelect";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { ValueType, ActionMeta } from "react-select/src/types";
import { ExpertRole, getExpertRoleTranslationKey, RoleWithExamsAndCourseTypes } from "../../../data/constants";
import { ExamTemplateIndex } from "../../../model/Exam";
import ExpertRoleExamsRow, { useExamColumnStyles } from "./ExpertRoleExamsRow";
import RoleSelectionDialog from "./RoleSelectionDialog";
import { useIntl } from "react-intl";
import { GetTranslation } from "../../../translations/GetTranslation";
import CustomButton from "../../common/buttons/CustomButton";
import { Add } from "@material-ui/icons";

type ExpertRolesTableProps = {
  currentRolesWithExamsAndCourseTypes: RoleWithExamsAndCourseTypes[];
  publishedExams: ExamTemplateIndex[];
  onRoleCreation: (role: ExpertRole[]) => void;
  onRoleDelete: (role: ExpertRole) => void;
  getExamChangeHandler: (
    role: ExpertRole
  ) => (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => void;
  getCourseTypeChangeHandler: (
    role: ExpertRole
  ) => (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => void;
  examSelectErrorsArray: ExpertRole[];
};

const ExpertRolesTable: React.FunctionComponent<ExpertRolesTableProps> = ({
  currentRolesWithExamsAndCourseTypes,
  publishedExams,
  getExamChangeHandler,
  getCourseTypeChangeHandler,
  onRoleCreation,
  onRoleDelete,
  examSelectErrorsArray,
}) => {
  const classes = useExamColumnStyles();
  const [isAddRoleDialogVisible, setIsAddRoleDialogVisible] = useState(false);
  const intl = useIntl();
  return (
    <Grid container={true} spacing={4}>
      <FormGridCell size="wide">
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30%">
                  <TranslatedMessage id="expert.field.role" />
                </TableCell>
                <TableCell width="60%" className={classes.noPaddingColumn}>
                  <TranslatedMessage id="application.moduleSelector.examsColumn" />
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <CustomButton
                    tooltip={<TranslatedMessage id="expert.field.role.add" />}
                    onClick={() => setIsAddRoleDialogVisible(true)}
                  >
                    <Add />
                  </CustomButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRolesWithExamsAndCourseTypes?.length ? (
                [...currentRolesWithExamsAndCourseTypes]
                  .sort((a, b) =>
                    GetTranslation(intl, getExpertRoleTranslationKey(a.role)).localeCompare(
                      GetTranslation(intl, getExpertRoleTranslationKey(b.role))
                    )
                  )
                  .map((roleExamsCourseTypes, index) => (
                    <ExpertRoleExamsRow
                      key={index}
                      role={roleExamsCourseTypes.role}
                      publishedExams={publishedExams}
                      selectedExamIds={roleExamsCourseTypes.examIds}
                      selectedCourseTypes={roleExamsCourseTypes.courseTypes}
                      onExamsChange={getExamChangeHandler(roleExamsCourseTypes.role)}
                      onCourseTypeChange={getCourseTypeChangeHandler(roleExamsCourseTypes.role)}
                      onRoleDelete={onRoleDelete}
                      examSelectError={examSelectErrorsArray.indexOf(roleExamsCourseTypes.role) !== -1}
                    />
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <TranslatedMessage id="application.moduleSelector.noAvaiableModules" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormGridCell>
      {isAddRoleDialogVisible && (
        <RoleSelectionDialog
          isOpened={isAddRoleDialogVisible}
          handleCancel={() => setIsAddRoleDialogVisible(false)}
          handleAccept={(selectedRoles) => {
            onRoleCreation(selectedRoles);
            setIsAddRoleDialogVisible(false);
          }}
          selectableRoles={getSelectableRoles(currentRolesWithExamsAndCourseTypes)}
        />
      )}
    </Grid>
  );
};

const getSelectableRoles = (selectedRoleExams: RoleWithExamsAndCourseTypes[]) => {
  const currentRoles = selectedRoleExams.map((roleExam) => roleExam.role);
  const existingExpertRoles = Object.values(ExpertRole);
  return existingExpertRoles.filter((rol) => currentRoles.indexOf(rol) === -1);
};

export default ExpertRolesTable;
